import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import SidePanel from '@components/shared/SidePanel/SidePanel';
import {
    selectIsReassignPaneOpen,
    selectIsChangeRecipientLoading,
    selectChangeRecipientError,
    setIsReassignPaneOpen,
    changeRecipient as changeRecipientAction
} from '@components/SignerUi/signerUiSlice';
import useEnforcedReassignAuth from '@components/SignerUi/hooks/useEnforcedReassignAuth';
import useTransactionIdFromUrl from '@components/SignerUi/hooks/useTransactionIdFromUrl';
import useBroadcaster from '@hooks/useBroadcaster';
import {getCeremonySummaryPath} from '@constants/routes';
import {BROADCAST_MESSAGES, REASSIGN_SCHEME_TYPES, REASSIGN_STEPS} from '@constants';
import {removeEmptyFields} from '@utils/removeEmptyFields';
import {
    getHasValidationErrors,
    getIsPhoneValid,
    getIsQnAChallengesValid
} from '@utils/formValidation';
import ReassignForm from './Steps/ReassignForm';
import ReassignAuth from './Steps/ReassignAuth';
import getSignerAuthByType from './utils';
import './reassignPanel.less';

export default function ReassignPanel() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const broadcast = useBroadcaster();
    const transactionId = useTransactionIdFromUrl();
    const isChangeRecipientLoading = useSelector(selectIsChangeRecipientLoading);
    const changeRecipientError = useSelector(selectChangeRecipientError);

    const hasLocalLanguage = false;
    const localLanguage = null;
    const shouldShowAuth = true;
    const maskResponse = null;

    const enforcedAuth = useEnforcedReassignAuth();
    const initialAuthType = enforcedAuth?.scheme || REASSIGN_SCHEME_TYPES.EMAIL;
    const initialSignerAuth = useMemo(
        () => getSignerAuthByType({type: initialAuthType, maskResponse}),
        [initialAuthType, maskResponse]
    );
    const defaultRecipient = {
        email: '',
        first_name: '',
        last_name: '',
        title: '',
        organisation: '',
        personal_message: ''
    };
    const [step, setStep] = useState(REASSIGN_STEPS.REASSIGN);
    const [recipient, setRecipient] = useState(defaultRecipient);
    const [auth, setAuth] = useState(initialSignerAuth);
    const isReassignPaneOpen = useSelector(selectIsReassignPaneOpen);

    function close() {
        dispatch(setIsReassignPaneOpen(false));
        setStep(REASSIGN_STEPS.REASSIGN);
        setRecipient(defaultRecipient);
    }

    function changeForm({target}) {
        setRecipient({
            ...recipient,
            [target.name]: target.value
        });
    }

    async function handleReassign() {
        const recipientData = {
            email: recipient.email,
            first_name: recipient.first_name || null,
            last_name: recipient.last_name || null,
            title: recipient.title || '',
            organisation: recipient.organisation || '',
            personal_message: recipient.personal_message || '',
            language: hasLocalLanguage ? localLanguage : undefined
        };

        if (shouldShowAuth && auth?.scheme) {
            if (auth.scheme === REASSIGN_SCHEME_TYPES.SMS) {
                const phoneNumber = auth.challenges?.[0]?.question;
                if (phoneNumber) {
                    recipientData.authentication = {
                        type: 'sms',
                        phone: phoneNumber
                    };
                }
            } else if (auth.scheme === REASSIGN_SCHEME_TYPES.QNA) {
                const validChallenges = auth.challenges?.filter(
                    (c) => c.question && c.answer
                );
                if (validChallenges && validChallenges.length > 0) {
                    recipientData.authentication = {
                        type: 'qa',
                        questions: validChallenges.map((c) => ({
                            type: 'string',
                            security_level: 'strict',
                            question: c.question,
                            answer: c.answer
                        }))
                    };
                }
            }
        }

        const filteredData = removeEmptyFields(recipientData);

        await dispatch(changeRecipientAction(filteredData)).unwrap();

        dispatch(setIsReassignPaneOpen(false));
        broadcast(BROADCAST_MESSAGES.TRANSACTIONS_UPDATED);

        const summaryParams = new URLSearchParams({
            first_name: recipient.first_name || '',
            last_name: recipient.last_name || '',
            email: recipient.email || ''
        });
        const summaryUrl = `${getCeremonySummaryPath(transactionId)}?${summaryParams.toString()}`;
        window.location.href = summaryUrl;
    }

    function submit(e) {
        e.preventDefault();

        switch (step) {
            case REASSIGN_STEPS.REASSIGN:
                if (shouldShowAuth) {
                    setStep(REASSIGN_STEPS.AUTH);
                } else {
                    handleReassign();
                }
                break;
            case REASSIGN_STEPS.AUTH:
                handleReassign();
                break;
            default:
                close();
                break;
        }
    }

    function cancel() {
        if (step === REASSIGN_STEPS.REASSIGN) {
            close();
        } else {
            setStep(REASSIGN_STEPS.REASSIGN);
        }
    }

    function getButtonLabel() {
        if (step === REASSIGN_STEPS.REASSIGN) {
            return shouldShowAuth
                ? intl.formatMessage({id: 'oss.components.Reassign.next', defaultMessage: ''})
                : intl.formatMessage({id: 'oss.components.Reassign.reassign', defaultMessage: ''});
        }

        return intl.formatMessage({id: 'oss.components.Reassign.reassign', defaultMessage: ''});
    }

    function getIsStepValid() {
        if (step === REASSIGN_STEPS.REASSIGN) {
            return !getHasValidationErrors(recipient);
        }

        if (step === REASSIGN_STEPS.AUTH) {
            switch (auth?.scheme) {
                case REASSIGN_SCHEME_TYPES.QNA:
                    return getIsQnAChallengesValid(auth?.challenges || []);
                case REASSIGN_SCHEME_TYPES.SMS:
                    return !!auth?.challenges?.[0]?.question && getIsPhoneValid(auth?.challenges?.[0]?.question);
                default:
                    return true;
            }
        }

        return true;
    }

    useEffect(() => {
        if (!isReassignPaneOpen) {
            setStep(REASSIGN_STEPS.REASSIGN);
            setRecipient(defaultRecipient);
            setAuth(initialSignerAuth);
        }
    }, [isReassignPaneOpen]);

    useEffect(() => {
        setAuth(initialSignerAuth);
    }, [initialSignerAuth]);

    const buttonsNode = (
        <>
            <AppOutlinedButton onClick={cancel}>
                {intl.formatMessage({id: 'oss.components.Reassign.cancel', defaultMessage: ''})}
            </AppOutlinedButton>
            <AppButton
                type="submit"
                disabled={!getIsStepValid()}
                data-wdio="test-reassign-form-submit-btn"
            >
                {getButtonLabel()}
            </AppButton>
        </>
    );

    return (
        <SidePanel
            className="reassign-signing-panel"
            open={isReassignPaneOpen}
            onClose={close}
            onSubmit={submit}
            isLoading={isChangeRecipientLoading}
            buttonsNode={buttonsNode}
            title={intl.formatMessage({id: 'oss.components.Reassign.title', defaultMessage: ''})}
            ModalProps={{keepMounted: false}}
        >
            <div className="reassign-signing-panel-container">
                {changeRecipientError && (
                    <div className="error-message" data-wdio="reassign-error">
                        {changeRecipientError.message}
                    </div>
                )}
                {step === REASSIGN_STEPS.REASSIGN && <ReassignForm form={recipient} changeForm={changeForm} />}
                {step === REASSIGN_STEPS.AUTH && <ReassignAuth auth={auth} enforcedAuth={enforcedAuth} setAuth={setAuth} />}
            </div>
        </SidePanel>
    );
}
